<template>
  <div class="min-h-screen">
    <HeroSection />

    <!-- Social Proof Section -->
    <AnimationComponent>
      <section class="py-[50px] md:py-24">
        <MerchantLogoCarousel variant="light" />
      </section>
    </AnimationComponent>

    <FeaturesSection
      :features="layupProducts"
      tag="Products"
      title="Progressive financial solutions that will fast-track your growth"
      description="Scale your business with smart new payment methods that expand your market and drive direct sales growth."
      hasButton
    />

    <TitleFeaturesSection
      :features="businessConnectFeatures"
      title="Payment infrastructure for recurring and pre-payment models"
      description="Automate collections, settlements, and reconciliation so you get predictable cash flow and clear financial visibility."
    />

    <PaymentMethodsSection
      title="Select from multiple payment methods"
      description="Enable the payment options that best suit your customer audience."
      link-to-page="business-pricing-layby"
    />

    <FeaturesSection
      :features="plugAndPlayFeatures"
      title="Plug and play, out-of-the-box"
      description="We provide API's that give businesses the flexibility they need to customise and scale their pre-payment services."
      hasButton
      class="mt-20"
    />

    <AnimationComponent>
      <InTheNews :remove-desktop-padding-top="false" />
    </AnimationComponent>

    <AnimationComponent>
      <section class="py-16 md:py-24 max-sm:px-6 max-xl:px-13">
        <div class="max-w-3xl mx-auto">
          <div class="flex flex-col gap-5 items-center max-w-3xl mx-auto mb-8 text-center">
            <h2 class="text-2xl md:text-4xl font-semibold leading-8 md:leading-11 tracking-[-0.02em] text-grey-dark-50">
              Frequently asked questions
            </h2>
            <p class="text-base md:text-xl leading-6 md:leading-[30px] text-grey-dark-300">
              Everything you need to know about the product and billing.
            </p>
          </div>
          <FaqGroup v-if="faqData" :items="faqData.items" />
          <div class="flex justify-center mt-8">
            <router-link to="/business/faq">
              <Button variant="outline-light" size="business">
                View more FAQ's
                <ArrowRight />
              </Button>
            </router-link>
          </div>
        </div>
      </section>
    </AnimationComponent>

    <AnimationComponent>
      <ProductShowcaseSection />
    </AnimationComponent>
  </div>
</template>

<script setup lang="ts">
import AnimationComponent from '@/components/AnimationComponent.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import HeroSection from '@/components/business/HeroSection.vue';
import PaymentMethodsSection from '@/components/business/PaymentMethodsSection.vue';
import ProductShowcaseSection from '@/components/business/ProductShowcaseSection.vue';
import TitleFeaturesSection from '@/components/business/TitleFeaturesSection.vue';
import FaqGroup from '@/components/FaqComponent.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import { Button } from '@/components/ui/button';
import questions from '@/data/questions.json';
import { ArrowRight } from 'lucide-vue-next';
import { computed } from 'vue';
import InTheNews from '@/components/business/InTheNews.vue';

const faqData = computed(() => {
  return questions.find((c) => c.category === 'business');
});

const layupProducts = [
  {
    icon: 'Calendar',
    title: 'Lay-by',
    description: 'Offer credit-free, interest-free payment plans to expand your customer base and grow sales.',
    buttonLabel: 'Learn More',
    buttonLink: '/business/layby',
  },
  {
    icon: 'PiggyBank',
    title: 'Save now, buy later',
    description:
      'BOOST Rewards enables your customers to save directly with your brand and earn rewards along the way via embedded vouchers.',
    buttonLabel: 'Learn More',
    buttonLink: '/business/save-now-buy-later',
  },
  // ANDREW DOESN'T WANT THESE FEATURES VISIBLE YET
  // {
  //   icon: 'CreditCard',
  //   title: 'Payment plans',
  //   description: 'Scale your business and remove the admin of managing and collecting payment plans.',
  // },
  // {
  //   icon: 'RefreshCw',
  //   title: 'Smart subscriptions',
  //   description: 'Create flexible, recurring payment plans for memberships, services, or ongoing product offerings.',
  // },
  // {
  //   icon: 'Users',
  //   title: 'Stokvels',
  //   description:
  //     'Digitise and automate group stokvels with our split payment feature and automated recurring collection management system.',
  // },
  // {
  //   icon: 'Package',
  //   title: 'Pre-orders',
  //   description: 'Use our latest pre-order payment solution for products that are upcoming or out of stock.',
  // },
];

const businessConnectFeatures = [
  {
    icon: 'Building2',
    title: 'Third-Party Payment Provider (TPPP)',
    description:
      'Securely initiate and manage customer payments on your behalf, with end-to-end controls that improve collection success and reduce operational overhead.',
  },
  {
    icon: 'Database',
    title: 'Treasury reconciliation engine',
    description:
      'Automatically reconcile transactions, settlements, and bank movements in near real time for accurate reporting and faster financial close.',
  },
  {
    icon: 'Repeat',
    title: 'Auto-collections engine',
    description:
      'Automate recurring and one-off collections via tokenised cards or account-to-account (A2A) payments, reducing failed payments and manual follow-ups.',
  },
  {
    icon: 'Zap',
    title: 'Auto-settlements engine',
    description:
      'Settle merchants daily, weekly, or monthly. We calculate payouts, apply fees, and automatically distribute funds, removing settlement admin.',
  },
];
const plugAndPlayFeatures = [
  {
    icon: 'Puzzle',
    title: 'Pre-built integrations',
    description: 'Connect LayUp to your business software for seamless, secure transactions.',
  },
  {
    icon: 'Headphones',
    title: 'Support Services',
    description: 'We help you simplify and automate processes, leaving time to focus on acquisition and growth.',
    buttonLabel: 'Learn More',
    buttonLink: '/business/contact-us',
  },
  {
    icon: 'HandCoins',
    title: 'Pre-payment products',
    description:
      "Offer digital lay-by, save-now-buy-later, powered by LayUp's end-to-end payment and reconciliation infrastructure.",
  },
];
</script>
